import { GetStaticPaths, GetStaticProps } from 'next';
import { Router } from 'next/router';
import React, { useEffect } from 'react';

import {
  getFollowingArticles,
  getInsightTypeFromParam,
} from 'containers/InsightPage/InsightPage.utils';
import ArticleImageBlock from 'containers/InsightPage/sections/ArticleImageBlock/ArticleImageBlock';
import ArticleNextSection from 'containers/InsightPage/sections/ArticleNextSection/ArticleNextSection';
import ArticleQuote from 'containers/InsightPage/sections/ArticleQuote/ArticleQuote';
import ArticleVideo from 'containers/InsightPage/sections/ArticleVideo/ArticleVideo';
import useOverElement from 'hooks/useOverElement';
import { useCopyStore, useGlobalStore } from 'store';
import { CopyStoreType } from 'store/copy.types';
import { queryCMS } from 'utils/cms/api';
import { insightTypeArticle } from 'utils/cms/gql';
import { ISR_TIMEOUT } from 'utils/config';
import { handleMouseEnter, handleMouseLeave } from 'utils/cursorGlobal';
import { ROUTES } from 'utils/routes';
import { marketPageId, MarketPages } from 'utils/sharedStrapiQueries/market';
import { InsightArticleType } from 'utils/sharedStrapiQueries/sharedTypes';
import { pageMotionProps } from 'utils/styles/animations';
import { ColorNames } from 'utils/styles/theme';

import { DataCMSTypes, ModuleListProps } from './InsightPage.types';
import ArticleHero from './sections/ArticleHero/ArticleHero';
import ArticleRichText from './sections/ArticleRichText/ArticleRichText';
import ArticlesSlider from './sections/ArticleSlider/ArticleSlider';

import * as S from './InsightPage.styles';

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  // @ts-ignore

  const id = await marketPageId(MarketPages.insightsPage, locale);
  const cookieId = await marketPageId(MarketPages.cookieBar, locale);
  const footerId = await marketPageId(MarketPages.footer, locale);

  const globalIds = { cookieId, footerId };

  const type = getInsightTypeFromParam(params?.insightType);

  const response = await queryCMS<DataCMSTypes>({
    query: insightTypeArticle({
      id,
      locale,
      slug: params?.slug,
      type,
      globalIds,
    }),
  });

  const fallbackNeeded = !response?.data?.[type]?.length;

  if (fallbackNeeded) {
    const response = await queryCMS<DataCMSTypes>({
      query: insightTypeArticle({
        id,
        locale,
        slug: params?.slug,
        type,
        globalIds,
        oldSlug: true,
      }),
    });

    const dataFallback = response.data?.[type]?.[0];

    if (dataFallback?.slug) {
      return {
        redirect: {
          destination: `/${params?.insightType}/${dataFallback.slug}`,
          permanent: true,
        },
      };
    }

    return { notFound: true };
  }

  const {
    data: { sharedCopies, redirectPopups, cookieBar, navbar, footer },
  } = response;

  const data: InsightArticleType = response.data[type][0];
  const articles: InsightArticleType[] = response.data.articles;

  if (!data || !sharedCopies) return { notFound: true };

  return {
    props: {
      initialCopy: {
        head: data?.seo || null,
        global: sharedCopies[0]?.global || null,
        globalSettings: sharedCopies[0] || null,
        redirectPopup: redirectPopups[0] || null,
        cookieBar: cookieBar || null,
        navbar,
        footer,
      },
      data,
      ...getFollowingArticles(articles, data),
      type: params?.insightType,
    },
    revalidate: ISR_TIMEOUT,
  };
};

interface InsightPageProps {
  initialCopy: {
    head: CopyStoreType['copy']['head'];
    global: CopyStoreType['copy']['global'];
    globalSettings: CopyStoreType['copy']['globalSettings'];
    redirectPopup: CopyStoreType['copy']['redirectPopup'];
    cookieBar: CopyStoreType['copy']['cookieBar'];
    navbar: CopyStoreType['copy']['navbar'];
    footer: CopyStoreType['copy']['footer'];
  };
  data: InsightArticleType;
  nextArticle: InsightArticleType;
  prevArticle: InsightArticleType;
  type: string;
  router: Router;
}

const InsightPage: React.FunctionComponent<InsightPageProps> = ({
  initialCopy,
  data,
  nextArticle,
  prevArticle,
  type,
}) => {
  const { setCopy } = useCopyStore();
  const { setHeaderText } = useGlobalStore();

  const [headlineRef] = useOverElement();
  const [nextRef] = useOverElement();

  useEffect(() => {
    setCopy(initialCopy);
    setHeaderText({
      default:
        data.headerText ||
        initialCopy?.navbar?.navLinks.find(
          ({ href }) => href === ROUTES.INSIGHTS
        )?.headerText,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCopy]);

  if (!data) return null;

  const modulesList: ModuleListProps = {
    ComponentSectionsArticleHero: ArticleHero,
    ComponentComponentsQuote: ArticleQuote,
    ComponentComponentsRichText: ArticleRichText,
    ComponentComponentsArticlesSlider: ArticlesSlider,
    ComponentSubComponentsImageBlock: ArticleImageBlock,
    ComponentSubComponentsVideo: ArticleVideo,
  };

  const renderModules = () =>
    data.modules.map((module, idx) => {
      const moduleName = module.__typename;
      const ModuleComponent = modulesList[moduleName];

      return (
        ModuleComponent && (
          <ModuleComponent key={`${module.__typename}${idx}`} data={module} />
        )
      );
    });

  return (
    <S.Wrapper {...pageMotionProps}>
      <S.HeadlineWrapper ref={headlineRef}>
        <S.InsightHeadline
          text={data.name || 'Article name'}
          color={ColorNames.lighterMineShaft}
        />
      </S.HeadlineWrapper>
      <S.SharePanel socials={data.socials} />
      {renderModules()}
      <S.LastSharePanel socials={data.socials} />
      <S.ReadMoreWrapper
        ref={nextRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ArticleNextSection
          copy={data.previousArticleText || 'Previous article'}
          title={prevArticle.name}
          url={`/${type}/${prevArticle.slug}`}
        />
        <ArticleNextSection
          copy={data.nextArticleText || 'Next article'}
          title={nextArticle.name}
          url={`/${type}/${nextArticle.slug}`}
        />
      </S.ReadMoreWrapper>
    </S.Wrapper>
  );
};

export default InsightPage;
